// Adam's TRJHS letter-day schedule calculator.
//
// Q1 ONLY — Engineering 7 and Health & Wellness are rotation classes and
// will change to new subjects (Tech, Music, PE, Art) next quarter. The
// school calendar doc doesn't list a quarter-end date, so watch for that
// and update ADAM_SUBJECTS below when Q2 starts.
//
// Two calendar entries needed a closer read of the sidebar list's exact
// wording rather than the calendar grid's cell colors:
//   - Oct 30 "Professional Development (PD)" has no "Early Release"
//     qualifier, same as the Aug 31/Sep 1 PD days — treated as a full
//     no-school day.
//   - Nov 11 (Veterans Day) is a plain named holiday like Labor Day/
//     Columbus Day/MLK Day, not qualified as "Early Release" — treated
//     as a full no-school day.
// If either of those is still wrong, adjust NO_SCHOOL_DATES / HALF_DAY_DATES below.

import { toDateOnly, isWeekend, fmt } from "./dateUtils";
import { isNoSchoolDay, isHalfDayDate } from "./noSchoolDays";
export { loadNoSchoolDays } from "./noSchoolDays";

// Source doc: full A–G rotation table this calculator is built from.
export const SCHEDULE_DOC_URL = "https://drive.google.com/file/d/1BvUNZBr4SLg_12hyY-6w3jrPb1835Mzy/view?usp=drivesdk";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];

// The first day of school (Sept 2, 2026) is Day A. Rotation counts forward
// through school days only (weekends and NO_SCHOOL_DATES are skipped).
const ROTATION_START = "2026-09-02";

// Full days with no school at all — skipped entirely from the rotation count.
const NO_SCHOOL_DATES = [
  "2026-08-31", "2026-09-01", // Full Day PD - No Classes
  "2026-09-04",               // No School for Staff & Students
  "2026-09-07",               // Labor Day
  "2026-10-12",               // Columbus Day / Indigenous Peoples Day
  "2026-10-30",               // Professional Development (PD) — no "Early Release" qualifier, same as Aug 31/Sep 1
  "2026-11-11",               // Veterans Day — named holiday, no "Early Release" qualifier
  "2026-11-26", "2026-11-27", // Thanksgiving Break
  "2026-12-24", "2026-12-25", "2026-12-28", "2026-12-29", "2026-12-30", "2026-12-31", // Christmas Vacation
  "2027-01-01",               // New Year's Day
  "2027-01-18",               // Martin Luther King Day
  "2027-02-15", "2027-02-16", "2027-02-17", "2027-02-18", "2027-02-19", // Feb Vacation
  "2027-04-19", "2027-04-20", "2027-04-21", "2027-04-22", "2027-04-23", // April Vacation
  "2027-05-31",               // Memorial Day
  "2027-06-18",               // Juneteenth
];

// School days that still count toward the rotation, but run on the
// Half Day bell schedule (shorter periods, no lunch break, early dismissal).
// These are all explicitly labeled "Early Release" or "Half Day" in the
// calendar's sidebar list.
const HALF_DAY_DATES = [
  "2026-09-24", // Early Release/Professional Dev.
  "2026-11-25", // Half Day
  "2027-01-15", // Early Release / Professional Dev.
  "2027-03-04", // Early Release / Professional Dev.
  "2027-03-25", // Early Release / Professional Dev.
  "2027-05-07", // Early Release / Professional Dev.
];

// Manually-added snow days / delay days. Add entries here when Carissa
// reports one — format: "YYYY-MM-DD": "noSchool" | "halfDay" | "oneHourDelay" | "twoHourDelay"
const MANUAL_OVERRIDES = {
  // "2027-01-20": "twoHourDelay",
};

// Beyond the hardcoded dates above (Tantasqua's originally-known calendar,
// kept as an always-working fallback), the "No School Days" Notion table
// (see noSchoolDays.js) is the day-to-day path for anything new — chiefly
// snow days, added there by Carissa (call/text from the school, not email
// — nothing automated) rather than as a code change + redeploy. Only
// Tantasqua/Both rows apply here — Adam's rotation shouldn't skip a day
// OSA is closed for but Tantasqua isn't (e.g. Oct 2, OSA-only). A Notion
// "Snow Day" row is treated as a full closure; if a snow day is actually
// a delay, add a MANUAL_OVERRIDES entry above instead so the right
// delay-length bell schedule is used — the table has no way to express
// delay length. Call loadNoSchoolDays() (re-exported above) once on
// dashboard load before the first read; isNoSchoolDay()/isHalfDayDate()
// below just read whatever's currently loaded, empty or not.

// Bell schedules — each is a list of blocks in slot order (Advisory and
// Dismissal aren't rotation slots, just bookends).
const SCHEDULES = {
  regular: {
    label: "Regular Day",
    advisory: { start: "7:30 AM", end: "7:33 AM" },
    slots: [
      { start: "7:36 AM", end: "8:23 AM" },
      { start: "8:26 AM", end: "9:12 AM" },
      { start: "9:15 AM", end: "10:02 AM" },
      { start: "10:05 AM", end: "10:52 AM" },
      { start: "11:28 AM", end: "12:15 PM", lunch: "10:55–11:25 AM" },
      { start: "12:18 PM", end: "1:04 PM" },
      { start: "1:07 PM", end: "1:51 PM" },
    ],
    dismissal: "1:51 PM",
  },
  halfDay: {
    label: "Half Day",
    advisory: { start: "7:30 AM", end: "7:33 AM" },
    slots: [
      { start: "7:34 AM", end: "8:02 AM" },
      { start: "8:03 AM", end: "8:30 AM" },
      { start: "8:31 AM", end: "8:58 AM" },
      { start: "8:59 AM", end: "9:26 AM" },
      { start: "9:27 AM", end: "9:54 AM" },
      { start: "9:55 AM", end: "10:23 AM" },
      { start: "10:24 AM", end: "10:51 AM" },
    ],
    dismissal: "10:51 AM",
  },
  // Delay-day bell times are approximate — the source schedule leaves the
  // block-letter order blank ("order depends on the day"), same rotation
  // formula still applies. Confirm actual dismissal/lunch details with the
  // school if a delay day ever comes up in practice.
  oneHourDelay: {
    label: "1-Hour Delay",
    advisory: { start: "8:30 AM", end: "8:33 AM" },
    slots: [
      { start: "8:34 AM", end: "9:13 AM" },
      { start: "9:14 AM", end: "9:53 AM" },
      { start: "9:54 AM", end: "10:33 AM" },
      { start: "10:34 AM", end: "11:49 AM", lunch: "~10:35–11:50 AM (grade 7/8 split)" },
      { start: "11:51 AM", end: "12:30 PM" },
      { start: "12:31 PM", end: "1:10 PM" },
      { start: "1:11 PM", end: "1:51 PM" },
    ],
    dismissal: "1:51 PM",
  },
  twoHourDelay: {
    label: "2-Hour Delay",
    advisory: { start: "9:30 AM", end: "9:33 AM" },
    slots: [
      { start: "9:34 AM", end: "10:04 AM" },
      { start: "10:05 AM", end: "10:35 AM" },
      { start: "10:35 AM", end: "11:48 AM", lunch: "~10:36–11:48 AM (grade 7/8 split)" },
      { start: "11:48 AM", end: "12:18 PM" },
      { start: "12:19 PM", end: "12:49 PM" },
      { start: "12:50 PM", end: "1:20 PM" },
      { start: "1:21 PM", end: "1:51 PM" },
    ],
    dismissal: "1:51 PM",
  },
};

// Adam's Q1 subject for each rotation block-letter (independent of which
// day/slot it lands in — see SKILL note at top about Q2 rotation classes).
const ADAM_SUBJECTS = {
  A: { subject: "Engineering 7", teacher: "Lanne", room: "B101" },
  B: { subject: "Health & Wellness", teacher: "Marcucci", room: null },
  C: { subject: "English 7", teacher: "Shemansky-Benoit", room: "C311" },
  D: { subject: "Social Studies 7", teacher: "Miarecki", room: "C307" },
  E: { subject: "Pre-Algebra 7", teacher: "Cofsky", room: "C320" },
  F: { subject: "Science 7", teacher: "Greene", room: "B309" },
  G: { subject: "Spanish 1A", teacher: "Feliciano", room: "C105" },
};

/**
 * Returns the rotation info for a given date (defaults to today), or null
 * fields if it's not a school day at all.
 *
 * Shape: { isSchoolDay, letterDay, scheduleType, scheduleLabel, periods: [{ start, end, letter, subject, teacher, room, lunch }] }
 */
function getTodaySchedule(dateStr) {
  const targetDateStr = dateStr || fmt(new Date());
  const override = MANUAL_OVERRIDES[targetDateStr];

  if (override === "noSchool" || NO_SCHOOL_DATES.includes(targetDateStr) || isNoSchoolDay(targetDateStr, "Tantasqua")) {
    return { isSchoolDay: false, letterDay: null, scheduleType: null, scheduleLabel: null, periods: [] };
  }

  const target = toDateOnly(targetDateStr);
  if (isWeekend(target)) {
    return { isSchoolDay: false, letterDay: null, scheduleType: null, scheduleLabel: null, periods: [] };
  }

  // Count school days from ROTATION_START up to and including target.
  let cursor = toDateOnly(ROTATION_START);
  let schoolDayIndex = 0;
  while (fmt(cursor) < targetDateStr) {
    cursor.setDate(cursor.getDate() + 1);
    const cStr = fmt(cursor);
    if (isWeekend(cursor)) continue;
    if (NO_SCHOOL_DATES.includes(cStr) || MANUAL_OVERRIDES[cStr] === "noSchool" || isNoSchoolDay(cStr, "Tantasqua")) continue;
    schoolDayIndex++;
  }

  const letterDay = LETTERS[schoolDayIndex % 7];
  const letterIndex = LETTERS.indexOf(letterDay);

  let scheduleType = "regular";
  if (override) scheduleType = override;
  else if (HALF_DAY_DATES.includes(targetDateStr) || isHalfDayDate(targetDateStr, "Tantasqua")) scheduleType = "halfDay";

  const schedule = SCHEDULES[scheduleType];
  const periods = schedule.slots.map((slot, i) => {
    const blockLetter = LETTERS[(letterIndex + i) % 7];
    const info = ADAM_SUBJECTS[blockLetter] || {};
    return { start: slot.start, end: slot.end, letter: blockLetter, subject: info.subject || null, teacher: info.teacher || null, room: info.room || null, lunch: slot.lunch || null };
  });

  return {
    isSchoolDay: true,
    letterDay,
    scheduleType,
    scheduleLabel: schedule.label,
    advisory: schedule.advisory,
    dismissal: schedule.dismissal,
    periods,
  };
}

export { getTodaySchedule };
